import { useRef, useState } from 'react'
import { HiEye, HiEyeOff } from 'react-icons/hi'
import api from '../utils/api'
import StrengthMeter from '../components/StrengthMeter'
import { useDebouncedEffect } from '../hooks/useDebounced'

const ATTACK_SCENARIOS = {
  online_throttled:   'Online, rate limited',
  online_unthrottled: 'Online, no throttling',
  offline_slow:       'Offline, bcrypt',
  offline_fast:       'Offline, GPU cluster',
}

const CHAR_CLASSES = [
  { label: 'lowercase', has: 'has_lowercase', count: 'lowercase_count' },
  { label: 'uppercase', has: 'has_uppercase', count: 'uppercase_count' },
  { label: 'digits',    has: 'has_digits',    count: 'digit_count' },
  { label: 'symbols',   has: 'has_symbols',   count: 'symbol_count' },
]

function Analyzer() {
  const [password, setPassword] = useState('')
  const [analysis, setAnalysis] = useState(null)
  const [error, setError]       = useState(false)
  const [show, setShow]         = useState(false)
  const requestSeq = useRef(0)

  // Analyze after the user pauses typing; sequence guard drops stale responses
  useDebouncedEffect(() => {
    if (!password) {
      setAnalysis(null)
      setError(false)
      return
    }
    const seq = ++requestSeq.current
    api.post('/analyze', { password })
      .then(res => {
        if (seq !== requestSeq.current) return
        setAnalysis(res.data)
        setError(false)
      })
      .catch(() => {
        if (seq !== requestSeq.current) return
        setError(true)
      })
  }, [password], 400)

  const patterns = analysis?.patterns
  const detectedPatterns = patterns?.has_patterns
    ? [
        { label: 'Keyboard walk', items: patterns.keyboard_patterns },
        { label: 'Repeated run',  items: patterns.repeated_chars },
        { label: 'Sequence',      items: patterns.sequential_chars },
        { label: 'Date',          items: patterns.date_patterns },
      ].filter(p => p.items?.length > 0)
    : []

  return (
    <div className="max-w-2xl mx-auto px-5 sm:px-8 py-10 sm:py-14">
      <header className="mb-8">
        <h1 className="font-display text-2xl font-semibold tracking-tight mb-1.5">Analyzer</h1>
        <p className="text-sm text-ink-muted">
          Entropy, structure, and time-to-crack. Runs as you type — nothing is stored.
        </p>
      </header>

      <div className="relative mb-2">
        <input
          type={show ? 'text' : 'password'}
          value={password}
          onChange={e => setPassword(e.target.value)}
          placeholder="Type a password…"
          aria-label="Password to analyze"
          className="input !py-3.5 !pr-12 font-mono !text-base"
          autoComplete="off"
          spellCheck="false"
          autoFocus
        />
        <button
          type="button"
          onClick={() => setShow(v => !v)}
          aria-label={show ? 'Hide password' : 'Show password'}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-ink-faint hover:text-ink-secondary transition-colors"
        >
          {show ? <HiEyeOff className="w-4 h-4" aria-hidden="true" /> : <HiEye className="w-4 h-4" aria-hidden="true" />}
        </button>
      </div>

      {error && (
        <p className="text-xs text-warn mb-2" role="status">
          Can’t reach the analysis API — retrying as you type.
        </p>
      )}

      {!password && (
        <div className="mt-14 text-center">
          <p className="text-sm text-ink-muted">Start typing to see the breakdown.</p>
          <p className="text-xs text-ink-faint mt-1.5">
            Try one you’d actually use — it never leaves the analysis request.
          </p>
        </div>
      )}

      {analysis && password && (
        <div className="mt-6 space-y-5 animate-fade-in">
          {/* Verdict */}
          <section className="card p-5">
            <StrengthMeter strength={analysis.strength} score={analysis.score} />

            <dl className="flex flex-wrap gap-x-8 gap-y-2 mt-5 pt-4 border-t border-surface-border">
              <div>
                <dt className="eyebrow">Entropy</dt>
                <dd className="font-mono text-sm mt-1">{analysis.entropy} bits</dd>
              </div>
              <div>
                <dt className="eyebrow">Length</dt>
                <dd className="font-mono text-sm mt-1">{analysis.length}</dd>
              </div>
              <div>
                <dt className="eyebrow">Unique</dt>
                <dd className="font-mono text-sm mt-1">
                  {analysis.characters?.unique_characters}
                  <span className="text-ink-faint"> / {analysis.length}</span>
                </dd>
              </div>
              <div>
                <dt className="eyebrow">Classes</dt>
                <dd className="flex gap-1.5 mt-1.5">
                  {CHAR_CLASSES.map(({ label, has, count }) => (
                    <span
                      key={label}
                      title={`${analysis.characters?.[count] ?? 0} ${label}`}
                      className={`text-[11px] font-mono px-1.5 py-0.5 rounded border ${
                        analysis.characters?.[has]
                          ? 'text-ink-secondary border-surface-edge bg-surface-muted'
                          : 'text-ink-faint border-surface-border line-through decoration-ink-faint'
                      }`}
                    >
                      {label.slice(0, 3)}
                    </span>
                  ))}
                </dd>
              </div>
            </dl>
          </section>

          {/* Red flags */}
          {(analysis.is_common || detectedPatterns.length > 0) && (
            <section className="card p-5 border-l-2 border-l-danger">
              <h2 className="eyebrow mb-3">Weaknesses</h2>
              <ul className="space-y-2 text-[13px]">
                {analysis.is_common && (
                  <li className="text-danger">
                    Appears in common-password lists — assume attackers try it first.
                  </li>
                )}
                {detectedPatterns.map(p => (
                  <li key={p.label} className="flex items-baseline gap-2">
                    <span className="text-ink-muted shrink-0 w-28">{p.label}</span>
                    <span className="font-mono text-warn break-all">{p.items.join('  ')}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Time to crack */}
          <section className="card p-5">
            <h2 className="eyebrow mb-3">Time to crack</h2>
            <dl>
              {Object.entries(ATTACK_SCENARIOS).map(([key, label], i, arr) => (
                <div
                  key={key}
                  className={`flex items-baseline justify-between py-2 text-[13px] ${
                    i < arr.length - 1 ? 'border-b border-surface-border' : ''
                  }`}
                >
                  <dt className="text-ink-muted">{label}</dt>
                  <dd className="font-mono text-ink">{analysis.crack_times?.[key] ?? '—'}</dd>
                </div>
              ))}
            </dl>
          </section>

          {/* What to do */}
          {analysis.recommendations?.length > 0 && (
            <section className="card p-5">
              <h2 className="eyebrow mb-3">Suggestions</h2>
              <ul className="space-y-1.5">
                {analysis.recommendations.map((rec, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-[13px] text-ink-secondary">
                    <span className="text-accent mt-px select-none" aria-hidden="true">›</span>
                    {rec}
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      )}
    </div>
  )
}

export default Analyzer
